import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, Image, Palette, CalendarDays, Ticket, Users, Settings, Tag,
  MessageSquare, Newspaper, HelpCircle, Zap, ArrowRight,
} from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import type { BilingualText } from '@/types';

interface SearchResult {
  id: string;
  type: string;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  editPath: string;
  matchField: string;
  matchText: string;
}

function bil(t: BilingualText | undefined): string {
  if (!t) return '';
  return `${t.en || ''} ${t.ka || ''}`.trim();
}

function matchText(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

interface AdminSearchProps {
  open: boolean;
  onClose: () => void;
}

const AdminSearch: React.FC<AdminSearchProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const artworks = useAdminStore((s) => s.artworks);
  const artists = useAdminStore((s) => s.artists);
  const exhibitions = useAdminStore((s) => s.exhibitions);
  const artFairs = useAdminStore((s) => s.artFairs);
  const teamMembers = useAdminStore((s) => s.teamMembers);
  const categories = useAdminStore((s) => s.categories);
  const dropshopCollections = useAdminStore((s) => s.dropshopCollections);
  const newsArticles = useAdminStore((s) => s.newsArticles);
  const faqs = useAdminStore((s) => s.faqs);
  const reviews = useAdminStore((s) => s.reviews);
  const settings = useAdminStore((s) => s.settings);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const results = useMemo((): SearchResult[] => {
    const q = query.trim();
    if (q.length < 2) return [];
    const items: SearchResult[] = [];

    // Artworks
    artworks.forEach((a) => {
      const fields = [
        { field: 'Title', text: bil(a.title) },
        { field: 'Description', text: bil(a.description) },
        { field: 'Medium', text: bil(a.medium) },
        { field: 'Category', text: a.category },
      ];
      for (const f of fields) {
        if (matchText(f.text, q)) {
          items.push({
            id: a._id, type: 'Artwork', icon: Image,
            title: bil(a.title) || 'Untitled',
            subtitle: a.category,
            editPath: `/admin/artworks/${a._id}`,
            matchField: f.field,
            matchText: f.text.substring(0, 100),
          });
          break;
        }
      }
    });

    // Artists
    artists.forEach((a) => {
      const fields = [
        { field: 'Name', text: bil(a.name) },
        { field: 'Bio', text: bil(a.bio) },
      ];
      for (const f of fields) {
        if (matchText(f.text, q)) {
          items.push({
            id: a._id, type: 'Artist', icon: Palette,
            title: bil(a.name),
            subtitle: '',
            editPath: `/admin/artists/${a._id}`,
            matchField: f.field,
            matchText: f.text.substring(0, 100),
          });
          break;
        }
      }
    });

    // Exhibitions
    exhibitions.forEach((e) => {
      const fields = [
        { field: 'Title', text: bil(e.title) },
        { field: 'Description', text: bil(e.description) },
        { field: 'Location', text: bil(e.location) },
      ];
      for (const f of fields) {
        if (matchText(f.text, q)) {
          items.push({
            id: e._id, type: 'Exhibition', icon: CalendarDays,
            title: bil(e.title),
            subtitle: bil(e.location),
            editPath: `/admin/exhibitions/${e._id}`,
            matchField: f.field,
            matchText: f.text.substring(0, 100),
          });
          break;
        }
      }
    });

    // Art Fairs
    artFairs.forEach((f) => {
      const fields = [
        { field: 'Title', text: bil(f.title) },
        { field: 'Description', text: bil(f.description) },
        { field: 'Location', text: bil(f.location) },
      ];
      for (const fd of fields) {
        if (matchText(fd.text, q)) {
          items.push({
            id: f._id, type: 'Art Fair', icon: Ticket,
            title: bil(f.title),
            subtitle: bil(f.location),
            editPath: `/admin/art-fairs/${f._id}`,
            matchField: fd.field,
            matchText: fd.text.substring(0, 100),
          });
          break;
        }
      }
    });

    // Team
    teamMembers.forEach((m) => {
      const fields = [
        { field: 'Name', text: bil(m.name) },
        { field: 'Role', text: bil(m.role) },
        { field: 'Bio', text: bil(m.bio) },
      ];
      for (const f of fields) {
        if (matchText(f.text, q)) {
          items.push({
            id: m._id, type: 'Team', icon: Users,
            title: bil(m.name),
            subtitle: bil(m.role),
            editPath: `/admin/team/${m._id}`,
            matchField: f.field,
            matchText: f.text.substring(0, 100),
          });
          break;
        }
      }
    });

    // Categories
    categories.forEach((c) => {
      if (matchText(bil(c.name), q)) {
        items.push({
          id: c._id, type: 'Category', icon: Tag,
          title: bil(c.name),
          subtitle: `${c.subcategories?.length || 0} subcategories`,
          editPath: '/admin/categories',
          matchField: 'Name',
          matchText: bil(c.name),
        });
      }
    });

    // Dropshop
    dropshopCollections.forEach((d) => {
      const fields = [
        { field: 'Title', text: bil(d.title) },
        { field: 'Description', text: bil(d.description) },
      ];
      for (const f of fields) {
        if (matchText(f.text, q)) {
          items.push({
            id: d._id, type: 'Dropshop', icon: Zap,
            title: bil(d.title),
            subtitle: '',
            editPath: `/admin/dropshop/${d._id}`,
            matchField: f.field,
            matchText: f.text.substring(0, 100),
          });
          break;
        }
      }
    });

    // News
    newsArticles.forEach((n) => {
      const fields = [
        { field: 'Title', text: bil(n.title) },
        { field: 'Content', text: bil(n.content) },
        { field: 'Excerpt', text: bil(n.excerpt) },
      ];
      for (const f of fields) {
        if (matchText(f.text, q)) {
          items.push({
            id: n._id, type: 'News', icon: Newspaper,
            title: bil(n.title),
            subtitle: '',
            editPath: `/admin/news/${n._id}`,
            matchField: f.field,
            matchText: f.text.substring(0, 100),
          });
          break;
        }
      }
    });

    // FAQ
    faqs.forEach((f) => {
      const fields = [
        { field: 'Question', text: bil(f.question) },
        { field: 'Answer', text: bil(f.answer) },
      ];
      for (const fd of fields) {
        if (matchText(fd.text, q)) {
          items.push({
            id: f._id, type: 'FAQ', icon: HelpCircle,
            title: bil(f.question).substring(0, 60),
            subtitle: '',
            editPath: '/admin/faq',
            matchField: fd.field,
            matchText: fd.text.substring(0, 100),
          });
          break;
        }
      }
    });

    // Reviews
    reviews.forEach((r) => {
      if (matchText(r.comment, q) || matchText(r.authorName, q)) {
        items.push({
          id: r._id, type: 'Review', icon: MessageSquare,
          title: `${r.authorName} — ${r.rating}★`,
          subtitle: r.comment.substring(0, 50),
          editPath: '/admin/reviews',
          matchField: matchText(r.authorName, q) ? 'Author' : 'Comment',
          matchText: matchText(r.authorName, q) ? r.authorName : r.comment.substring(0, 100),
        });
      }
    });

    // Settings
    const settingsFields = [
      { field: 'Gallery Name', text: bil(settings.galleryName) },
      { field: 'Gallery Description', text: bil(settings.galleryDescription) },
      { field: 'Footer Text', text: bil(settings.footerText) },
      { field: 'Hero Title', text: settings.heroContent?.title || '' },
      { field: 'Hero Subtitle', text: bil(settings.heroContent?.subtitle) },
      { field: 'Contact Email', text: settings.contactInfo?.email || '' },
      { field: 'Contact Phone', text: settings.contactInfo?.phone || '' },
      { field: 'Contact Address', text: bil(settings.contactInfo?.address) },
      { field: 'Contact Hours', text: bil(settings.contactInfo?.hours) },
    ];
    for (const f of settingsFields) {
      if (f.text && matchText(f.text, q)) {
        items.push({
          id: `settings-${f.field}`, type: 'Settings', icon: Settings,
          title: f.field,
          subtitle: f.text.substring(0, 60),
          editPath: '/admin/settings',
          matchField: f.field,
          matchText: f.text.substring(0, 100),
        });
      }
    }

    return items.slice(0, 30);
  }, [query, artworks, artists, exhibitions, artFairs, teamMembers, categories, dropshopCollections, newsArticles, faqs, reviews, settings]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  const goToResult = useCallback((result: SearchResult) => {
    navigate(result.editPath);
    onClose();
  }, [navigate, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      // Scroll into view
      const el = listRef.current?.children[Math.min(selectedIndex + 1, results.length - 1)] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
      const el = listRef.current?.children[Math.max(selectedIndex - 1, 0)] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      goToResult(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Highlight matching text
  const highlight = (text: string, q: string): React.ReactNode => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="bg-yellow-200/60 text-black rounded-sm px-0.5">{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh] bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-[600px] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-black/[0.06]">
              <Search size={18} className="text-black/25 shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search content across the site..."
                className="flex-1 text-sm font-medium outline-none placeholder:text-black/25 bg-transparent"
                autoComplete="off"
                spellCheck={false}
              />
              {query && (
                <button onClick={() => setQuery('')} className="text-black/20 hover:text-black/50 transition-colors">
                  <X size={14} />
                </button>
              )}
              <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[8px] font-bold text-black/20 bg-black/[0.04] rounded-md">
                ESC
              </kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-[50vh] overflow-y-auto">
              {query.length < 2 ? (
                <div className="py-10 text-center">
                  <Search size={24} className="text-black/10 mx-auto mb-2" />
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/20">
                    Type to search content
                  </p>
                  <p className="text-[9px] text-black/15 mt-1">
                    Artworks, artists, exhibitions, settings, and more
                  </p>
                </div>
              ) : results.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/20">
                    No results found
                  </p>
                  <p className="text-[9px] text-black/15 mt-1">
                    Try different keywords
                  </p>
                </div>
              ) : (
                <div className="py-1">
                  {results.map((result, i) => (
                    <button
                      key={result.id}
                      onClick={() => goToResult(result)}
                      onMouseEnter={() => setSelectedIndex(i)}
                      className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                        i === selectedIndex ? 'bg-black/[0.04]' : 'hover:bg-black/[0.02]'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        i === selectedIndex ? 'bg-black text-white' : 'bg-black/[0.04] text-black/30'
                      }`}>
                        <result.icon size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold truncate">
                            {highlight(result.title, query)}
                          </span>
                          <span className="text-[8px] font-bold uppercase tracking-wider text-black/25 bg-black/[0.04] px-1.5 py-0.5 rounded shrink-0">
                            {result.type}
                          </span>
                        </div>
                        <p className="text-[10px] text-black/35 truncate mt-0.5">
                          {result.matchField}: {highlight(result.matchText, query)}
                        </p>
                      </div>
                      <ArrowRight size={12} className={`shrink-0 transition-colors ${
                        i === selectedIndex ? 'text-black/40' : 'text-black/10'
                      }`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {results.length > 0 && (
              <div className="px-5 py-2.5 border-t border-black/[0.06] flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-[8px] font-bold text-black/20 uppercase tracking-wider">
                  <kbd className="px-1 py-0.5 bg-black/[0.04] rounded text-[7px]">↑↓</kbd> Navigate
                </div>
                <div className="flex items-center gap-1.5 text-[8px] font-bold text-black/20 uppercase tracking-wider">
                  <kbd className="px-1 py-0.5 bg-black/[0.04] rounded text-[7px]">Enter</kbd> Open
                </div>
                <span className="text-[8px] text-black/15 ml-auto">{results.length} result{results.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdminSearch;
